<template>
  <div>
    <PageLoader :storage="appLoading" />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showSnackBar" color="#427D2D" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color:#fff">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color:#fff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <div class="full-screen-map-wrapper">
      <BaseLeafletMap
        title="Snake Reports Map"
        :layers="mappedLayers"
        :statistics="mapStats"
        :loading="appLoading"
        :toolbar="{ search: true, fullscreen: true, fitBounds: true, satellite: true }"
        height="100vh"
        @marker-click="onMarkerClick"
            >
            </BaseLeafletMap>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import BaseLeafletMap     from '@/components/maps/BaseLeafletMap.vue';
import SnakeReportPopup   from '@/components/maps/popups/SnakeReportPopup.vue';
import { layerFactory }   from '@/components/maps/utils/layerFactory';
import { LAYER_TYPES }    from '@/components/maps/constants/layerTypes';
import { STATUS_COLORS } from '@/components/maps/constants/statusColors';
import { createMarkerIcon } from '@/components/maps/utils/markerFactory';
import { MARKER_TYPES }     from '@/components/maps/constants/markerTypes';

export default {
  components: { BaseLeafletMap },

  data() {
    return {
      appLoading:      false,
      ServerError:     false,
      showSnackBar:    false,
      timeout:         5000,
      msg:             null,
      data:            [],
      snakeitems:      [],
      userslist:       [],
      totalcount:      0,

      // ── Filter state (preserved exactly) ──────────────────────────────
      timeperiod:      false,
      bigsnakes:       false,
      allsnakes:       false,
      users:           false,
      alldata:         true,
      activePicker:    null,
      fromdate:        null,
      menu:            false,
      activePickerto:  null,
      todate:          null,
      menu2:           false,
      bigsnakeselect:  '',
      bigsnakestypes: [
        { text: 'spectacled cobra',  value: '5c98a6f9db9c5d619e5a721c' },
        { text: 'common krait',      value: '5c98a7f9db9c5d619e5a721d' },
        { text: "russell's viper",   value: '5c988da5db9c5d619e5a7218' },
        { text: 'saw scaled viper',  value: '5c988c39db9c5d619e5a7217' },
      ],
      allsnakelist:    '',
      selecteduser:    '',

      // Internal API selected values
      selectedfromdate:    undefined,
      selectedtodate:      undefined,
      big4snake:           undefined,
      selectedallsnakelist:undefined,
      selectedusername:    undefined,
    };
  },

  computed: {
    statusCounts() {
      const counts = { pending: 0, rescued: 0, released: 0, handedOver: 0, cancelled: 0 };
      this.data.forEach(r => {
        const s = (r.situation || r.status || '').toLowerCase();
        if (s.includes('pending'))  counts.pending++;
        else if (s.includes('rescued')) counts.rescued++;
        else if (s.includes('released')) counts.released++;
        else if (s.includes('handed'))  counts.handedOver++;
        else if (s.includes('cancel'))  counts.cancelled++;
        else counts.pending++;
      });
      return counts;
    },

    mappedLayers() {
      return [
        layerFactory({
          id:         LAYER_TYPES.SNAKE_REPORTS,
          title:      'Snake Reports',
          color:      STATUS_COLORS.PENDING,
          clustered:  false,
          searchable: true,
        legend: [
            { color: STATUS_COLORS.PENDING,     label: 'Pending' },
            { color: STATUS_COLORS.RESCUED,     label: 'Rescued' },
            { color: STATUS_COLORS.RELEASED,    label: 'Released' },
            { color: STATUS_COLORS.HANDED_OVER, label: 'Handed Over' },
            { color: STATUS_COLORS.CANCELLED,   label: 'Cancelled' },
          ],
          markers: (() => {
            const rawMarkers = this.data
              .filter(r => r.location && r.location.length === 2)
              .map(r => {
              const status = r.situation || r.status || 'pending';
              let color = STATUS_COLORS.PENDING;
              const s = status.toLowerCase();
              if (s.includes('rescued')) color = STATUS_COLORS.RESCUED;
              else if (s.includes('released')) color = STATUS_COLORS.RELEASED;
              else if (s.includes('handed')) color = STATUS_COLORS.HANDED_OVER;
              else if (s.includes('cancel')) color = STATUS_COLORS.CANCELLED;

              return {
                id:           r._id,
                lat:          r.location[1],
                lng:          r.location[0],
                layer:        LAYER_TYPES.SNAKE_REPORTS,
                status,
                popup:        SnakeReportPopup,
                searchFields: [
                  r.rescue && r.rescue.snake && r.rescue.snake.name,
                  r.district,
                  r.user && r.user.name,
                  r._id,
                ].filter(Boolean),
                data: r,
                icon: createMarkerIcon(MARKER_TYPES.SNAKE_REPORT, color),
              };
            });
            
            const locGroups = {};
            rawMarkers.forEach(m => {
              const key = `${m.lat},${m.lng}`;
              if (!locGroups[key]) locGroups[key] = [];
              locGroups[key].push(m);
            });
            
            const finalMarkers = [];
            Object.values(locGroups).forEach(group => {
              if (group.length === 1) {
                finalMarkers.push(group[0]);
              } else {
                const radius = 0.00015 + (group.length * 0.000005);
                group.forEach((m, i) => {
                  const angle = (i / group.length) * 2 * Math.PI;
                  m.lat += Math.cos(angle) * radius;
                  m.lng += Math.sin(angle) * radius;
                  finalMarkers.push(m);
                });
              }
            });
            return finalMarkers;
          })(),
        }),
      ];
    },

    mapStats() {
      const c = this.statusCounts;
      return [
        { title: 'Total Reports', value: this.totalcount, icon: 'mdi-alert-circle-outline', color: STATUS_COLORS.PENDING     },
        { title: 'Pending',       value: c.pending,       icon: 'mdi-clock-outline',        color: STATUS_COLORS.PENDING     },
        { title: 'Rescued',       value: c.rescued,       icon: 'mdi-check-circle',         color: STATUS_COLORS.RESCUED     },
        { title: 'Released',      value: c.released,      icon: 'mdi-arrow-up-circle',      color: STATUS_COLORS.RELEASED    },
        { title: 'Handed Over',   value: c.handedOver,    icon: 'mdi-hand-extended',        color: STATUS_COLORS.HANDED_OVER },
        { title: 'Cancelled',     value: c.cancelled,     icon: 'mdi-close-circle',         color: STATUS_COLORS.CANCELLED   },
      ];
    },
  },

  watch: {
    menu(val)  { val && setTimeout(() => (this.activePicker   = 'YEAR')); },
    menu2(val) { val && setTimeout(() => (this.activePickerto = 'YEAR')); },
  },

  beforeMount() {
    this.getData();
    this.getList();
    this.find();
  },

  methods: {
    save(fromdate)  { this.$refs.menu.save(fromdate); },
    saveto(todate)  { this.$refs.menu2.save(todate); },

    checkUpdate(type) {
      if (type === 'all' && this.alldata) {
        this.timeperiod = false; this.bigsnakes = false;
        this.allsnakes  = false; this.users     = false;
        this.fromdate   = null;  this.todate    = null;
        this.bigsnakeselect = []; this.allsnakelist = []; this.selecteduser = '';
        this.find(); this.getData();
      } else if (type === 'other') {
        this.alldata = false;
      }
      if (!this.timeperiod && !this.bigsnakes && !this.allsnakes && !this.users && !this.alldata) {
        this.alldata = true; this.getData();
      }
    },

    reset() {
      this.alldata = true; this.timeperiod = false;
      this.bigsnakes = false; this.allsnakes = false; this.users = false;
      this.fromdate  = null; this.todate    = null;
      this.bigsnakeselect = []; this.allsnakelist = []; this.selecteduser = '';
      this.selectedfromdate = undefined; this.selectedtodate = undefined;
      this.big4snake = undefined; this.selectedallsnakelist = undefined;
      this.selectedusername = undefined;
      this.getData();
    },

    getData() {
      this.appLoading = true;
      axios({
        method:  'GET',
        url:     '/user/rescued/all/',
        headers: { 'x-auth-token': localStorage.getItem('token') },
      })
        .then(response => {
          if (response.data.status === true) {
            this.userslist = response.data.data;
          } else {
            this.msg = response.data.msg; this.showSnackBar = true;
          }
        })
        .catch(err => { this.ServerError = true; console.log(err); })
        .finally(() => { this.appLoading = false; });
    },

    find() {
      if (this.fromdate)              this.selectedfromdate     = this.fromdate;
      if (this.todate)                this.selectedtodate       = this.todate;
      if (this.bigsnakeselect && this.bigsnakeselect.length > 0) this.big4snake = this.bigsnakeselect;
      if (this.allsnakelist && this.allsnakelist.length > 0)     this.selectedallsnakelist = this.allsnakelist;
      if (this.selecteduser)          this.selectedusername     = this.selecteduser;

      this.appLoading = true;
      axios({
        method:  'POST',
        url:     '/location/find/v2',
        headers: { 'x-auth-token': localStorage.getItem('token') },
        data: {
          from:     this.selectedfromdate,
          to:       this.selectedtodate,
          big4:     this.big4snake,
          snakeid:  this.selectedallsnakelist,
          username: this.selectedusername,
        },
      })
        .then(response => {
          if (response.data.status === true) {
            this.data       = response.data.data.records || [];
            this.totalcount = this.data.length;
          } else {
            this.msg = response.data.msg; this.showSnackBar = true;
          }
        })
        .catch(err => { this.ServerError = true; console.log(err); })
        .finally(() => { this.appLoading = false; });
    },

    getList() {
      axios({
        method:  'GET',
        url:     '/snake/all/',
        headers: { 'x-auth-token': localStorage.getItem('token') },
      })
        .then(response => {
          if (response.data.status === true) this.snakeitems = response.data.data;
        })
        .catch(err => console.log(err));
    },

    onMarkerClick({ data }) {
      console.log('Snake report clicked:', data._id);
    },

    formatDate(date) {
      if (!date) return '-';
      const [year, month, day] = date.slice(0, 10).split('-');
      return `${day}-${month}-${year}`;
    },
  },
};
</script>
<style scoped>
.full-screen-map-wrapper {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
}
</style>

<style scoped>
.heading    { font-family: poppinsregular; font-size: 20px; font-weight: 900; }
.subheading { font-family: poppinsregular; font-size: 15px; font-weight: 500; }
</style>